import React, { useEffect, useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiChevronLeft,
  FiChevronRight,
  FiEdit3,
  FiPlus,
  FiSave,
  FiShare2,
  FiThumbsDown,
  FiThumbsUp,
  FiTrash2
} = FiIcons;

const favoriteLayoutStyles = `
  .favorite-humor-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0;
  }

  .favorite-humor-content .personal-carousel {
    min-width: 0;
    padding: 0 20px;
  }

  .favorite-humor-content .humor-editor {
    min-width: 0;
    margin: 0 28px;
  }

  .favorite-humor-content > .compact-add-entry {
    margin: 0 28px;
  }

  @media (max-width: 620px) {
    .favorite-humor-content .personal-carousel {
      padding-inline: 12px;
    }

    .favorite-humor-content > .compact-add-entry,
    .favorite-humor-content .humor-editor {
      margin-inline: 20px;
    }
  }
`;

function formatEntryDate(value) {
  if (!value) return null;

  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(new Date(value));
}

function PersonalHumorTile({
  type,
  pinnedEntry,
  entries,
  ratings,
  onSave,
  onDelete,
  onRate,
  onShare,
  isShared
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [position, setPosition] = useState(0);

  const isFavorite = type === 'favorite';
  const label = isFavorite ? 'Mein Lieblingswitz' : 'Stilblüte des Tages';
  const promptLabel = isFavorite
    ? 'Lieblingswitz hinzufügen'
    : 'Stilblüte hinzufügen';
  const hint = isFavorite
    ? 'Einen persönlichen Lieblingswitz festhalten'
    : 'Das Lustigste des heutigen Tages festhalten';

  const allEntries = useMemo(
    () => pinnedEntry ? [pinnedEntry, ...entries] : entries,
    [entries, pinnedEntry]
  );

  const visibleEntries = useMemo(
    () => allEntries
      .filter((entry) => ratings[entry.id] !== 'down')
      .sort((first, second) => {
        if (first.isDefault) return -1;
        if (second.isDefault) return 1;

        return new Date(second.createdAt || 0)
          - new Date(first.createdAt || 0);
      }),
    [allEntries, ratings]
  );

  const hiddenEntries = useMemo(
    () => allEntries.filter((entry) => ratings[entry.id] === 'down'),
    [allEntries, ratings]
  );

  useEffect(() => {
    setPosition((current) => (
      visibleEntries.length ? current % visibleEntries.length : 0
    ));
  }, [visibleEntries.length]);

  const move = (direction) => {
    if (!visibleEntries.length) return;

    setPosition((current) => {
      const next = current + direction;

      if (next < 0) return visibleEntries.length - 1;
      if (next >= visibleEntries.length) return 0;

      return next;
    });
  };

  const save = () => {
    if (!text.trim()) return;

    onSave({
      id: `${type}-${Date.now()}`,
      title: title.trim() || label,
      text: text.trim(),
      createdAt: new Date().toISOString()
    });

    setTitle('');
    setText('');
    setEditing(false);
    setPosition(1);
  };

  return (
    <>
      {isFavorite && <style>{favoriteLayoutStyles}</style>}

      <div className={`personal-humor-content ${
        isFavorite ? 'favorite-humor-content' : ''
      }`}>
        <div className="personal-carousel">
          <button
            className="humor-arrow personal-arrow"
            type="button"
            onClick={() => move(-1)}
            aria-label="Vorherigen Eintrag anzeigen"
            disabled={visibleEntries.length < 2}
          >
            <SafeIcon icon={FiChevronLeft} aria-hidden="true" />
          </button>

          <div className="personal-humor-viewport">
            <div
              className="personal-humor-track"
              style={{ transform: `translateX(-${position * 100}%)` }}
            >
              {visibleEntries.map((entry) => {
                const rating = ratings[entry.id];
                const shared = isShared(entry);
                const date = type === 'blossom'
                  ? formatEntryDate(entry.createdAt)
                  : null;

                return (
                  <article
                    className={`humor-tile humor-tile-custom ${
                      entry.isDefault ? 'default-favorite-tile' : ''
                    }`}
                    key={entry.id}
                  >
                    <div className="humor-tile-icon" aria-hidden="true">✎</div>
                    <h3>{entry.title}</h3>
                    {date && <time dateTime={entry.createdAt}>{date}</time>}
                    <p>{entry.text}</p>

                    <div
                      className="humor-rating"
                      aria-label={`Bewertung für ${entry.title}`}
                    >
                      <button
                        className={rating === 'up' ? 'rated' : ''}
                        type="button"
                        onClick={() => onRate(
                          entry.id,
                          rating === 'up' ? null : 'up'
                        )}
                        aria-label="Diesen Eintrag mag ich"
                        aria-pressed={rating === 'up'}
                      >
                        <SafeIcon icon={FiThumbsUp} aria-hidden="true" />
                      </button>

                      <button
                        className="humor-dislike-button"
                        type="button"
                        onClick={() => onRate(entry.id, 'down')}
                        aria-label="Diesen Eintrag ausblenden"
                      >
                        <SafeIcon icon={FiThumbsDown} aria-hidden="true" />
                      </button>

                      <button
                        className={`humor-share-button ${
                          shared ? 'shared' : ''
                        }`}
                        type="button"
                        onClick={() => onShare(entry)}
                        aria-label={shared
                          ? `${entry.title} nicht mehr teilen`
                          : `${entry.title} mit anderen teilen`}
                        aria-pressed={shared}
                      >
                        <SafeIcon icon={FiShare2} aria-hidden="true" />
                      </button>
                    </div>

                    {!entry.isDefault && (
                      <button
                        className="humor-delete"
                        type="button"
                        onClick={() => onDelete(entry.id)}
                        aria-label={`${entry.title} löschen`}
                      >
                        <SafeIcon icon={FiTrash2} aria-hidden="true" />
                      </button>
                    )}
                  </article>
                );
              })}
            </div>
          </div>

          <button
            className="humor-arrow personal-arrow"
            type="button"
            onClick={() => move(1)}
            aria-label="Nächsten Eintrag anzeigen"
            disabled={visibleEntries.length < 2}
          >
            <SafeIcon icon={FiChevronRight} aria-hidden="true" />
          </button>
        </div>

        {hiddenEntries.length > 0 && (
          <div className="hidden-personal-list">
            <p>Ausgeblendete Einträge</p>
            {hiddenEntries.map((entry) => (
              <button
                className="hidden-personal-item"
                type="button"
                key={entry.id}
                onClick={() => onRate(entry.id, null)}
              >
                <strong>{entry.title}</strong>
                <span>Wieder anzeigen</span>
              </button>
            ))}
          </div>
        )}

        {editing ? (
          <div className="humor-editor">
            <div className="humor-editor-heading">
              <SafeIcon icon={FiEdit3} aria-hidden="true" />
              <strong>{label} eintragen</strong>
            </div>

            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Überschrift (optional)"
              maxLength="80"
            />

            <textarea
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="Deinen Text hier eintragen …"
              rows="4"
              maxLength="600"
            />

            <div className="humor-editor-actions">
              <button
                className="button button-primary"
                type="button"
                onClick={save}
                disabled={!text.trim()}
              >
                <SafeIcon icon={FiSave} aria-hidden="true" />
                Speichern
              </button>

              <button
                className="button button-secondary"
                type="button"
                onClick={() => setEditing(false)}
              >
                Abbrechen
              </button>
            </div>
          </div>
        ) : (
          <button
            className="compact-add-entry"
            type="button"
            onClick={() => setEditing(true)}
          >
            <SafeIcon icon={FiPlus} aria-hidden="true" />
            <span>{promptLabel}</span>
          </button>
        )}
      </div>
    </>
  );
}

export default PersonalHumorTile;